import React from "react";
import {
  FaCheckCircle,
  FaInfoCircle,
  FaMedkit,
  FaTimesCircle,
  FaUser,
} from "react-icons/fa";
import {
  cn,
  getAdherentStatusMeta,
  getDefaultAdherentAvatar,
} from "../utils/attendancePageUtils";

const ACTIONS = [
  {
    value: "Présent",
    Icon: FaCheckCircle,
    activeClass: "bg-green-500 text-white",
    idleClass:
      "bg-transparent text-slate-600 hover:bg-slate-300/60 dark:text-slate-300 dark:hover:bg-slate-600/40",
  },
  {
    value: "Absent",
    Icon: FaTimesCircle,
    activeClass: "bg-red-500 text-white",
    idleClass:
      "bg-transparent text-slate-600 hover:bg-slate-300/60 dark:text-slate-300 dark:hover:bg-slate-600/40",
  },
  {
    value: "Malade/Blessé",
    Icon: FaMedkit,
    activeClass: "bg-amber-500 text-white",
    idleClass:
      "bg-transparent text-slate-600 hover:bg-slate-300/60 dark:text-slate-300 dark:hover:bg-slate-600/40",
  },
];

function getStatusBadgeClass(status) {
  switch (status) {
    case "Inscrit":
      return "bg-yellow-500 text-white";
    case "Cours d'Essai":
      return "bg-pink-500 text-white";
    case "Licencié":
      return "bg-green-700 text-white";
    case "Licencié hors club":
      return "bg-green-500 text-white";
    case "Non inscrit":
      return "bg-purple-500 text-white";
    case "Arrêt":
      return "bg-slate-800 text-white";
    default:
      return "bg-slate-500 text-white";
  }
}

const AttendanceMemberCard = ({
  adherent,
  attendanceStatus = "",
  onOpenInfo,
  onUpdateAttendance,
  saving = false,
}) => {
  const adherentStatusMeta = getAdherentStatusMeta(adherent?.statut_inscription);
  const avatar = getDefaultAdherentAvatar(adherent);

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
      <div className="grid min-h-[160px] grid-cols-[138px_minmax(0,1fr)_64px] sm:min-h-[170px] sm:grid-cols-[160px_minmax(0,1fr)_66px]">
        {/* Image */}
        <div className="h-full bg-slate-200 dark:bg-slate-700">
          {avatar ? (
            <img
              src={avatar}
              alt={`${adherent?.prenom || ""} ${adherent?.nom || ""}`}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-400 dark:text-slate-500">
              <FaUser className="text-4xl" />
            </div>
          )}
        </div>

        {/* Contenu central */}
        <div className="flex min-w-0 flex-col justify-between bg-slate-100 p-4 dark:bg-slate-100/5">
          <div>
            <h3 className="line-clamp-2 text-lg font-semibold leading-tight text-slate-700 dark:text-slate-100 sm:text-[17px]">
              {adherent?.nom} {adherent?.prenom}
            </h3>

            <div className="mt-4">
              <span
                className={cn(
                  "inline-flex min-w-[158px] items-center justify-center rounded-[2px] px-3 py-1.5 text-sm font-bold",
                  getStatusBadgeClass(adherentStatusMeta.label)
                )}
              >
                {adherentStatusMeta.label}
              </span>
            </div>
          </div>

          <div className="pt-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenInfo(adherent);
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-white shadow-md transition hover:bg-blue-600"
              aria-label={`Voir les informations de ${adherent?.prenom || ""} ${adherent?.nom || ""}`}
            >
              <FaInfoCircle />
            </button>
          </div>
        </div>

        {/* Colonne actions appel */}
        <div className="flex h-full flex-col border-l border-slate-200 bg-slate-200 dark:border-slate-700 dark:bg-slate-700/80">
          {ACTIONS.map(({ value, Icon, activeClass, idleClass }) => {
            const isActive = attendanceStatus === value;

            return (
              <button
                key={value}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateAttendance(adherent.id, value);
                }}
                disabled={saving}
                title={value}
                aria-label={value}
                className={cn(
                  "flex flex-1 items-center justify-center text-[30px] transition first:rounded-none last:rounded-none",
                  saving
                    ? "cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                    : isActive
                    ? activeClass
                    : idleClass
                )}
              >
                <Icon />
              </button>
            );
          })}
        </div>
      </div>
    </article>
  );
};

export default AttendanceMemberCard;